import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ClipboardCheck,
  TrendingUp,
  TrendingDown,
  ArrowDownUp,
  ChevronRight,
  Package,
  RefreshCw,
  Boxes,
  Stethoscope,
  FileSpreadsheet,
} from 'lucide-react'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { DateQuickButtons } from '../../components/ui/DateField'

interface DocLine {
  itmid: string
  sku: string
  name: string
  lot: string
  qty: number
  unitCost: number
  value: number
}
interface InvDoc {
  docNo: string
  docType: string
  docName: string
  date: string
  partner: string
  lines: DocLine[]
  plusValue: number
  minusValue: number
  netValue: number
  plusItems: number
  minusItems: number
}
interface InvDiffResponse {
  range: { from: string; to: string }
  totals: {
    docsCount: number
    itemsCount: number
    totalPlus: number
    totalMinus: number
    net: number
  }
  documents: InvDoc[]
}

const ron = new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' })
const num = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 3 })
function fmtRon(v: number): string {
  return ron.format(v || 0)
}
function fmtDate(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso.length <= 10 ? iso + 'T00:00:00' : iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' })
}

function firstOfMonthIso(): string {
  const n = new Date()
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-01`
}
function todayIso(): string {
  const n = new Date()
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`
}

function KpiCard({
  label,
  value,
  sub,
  tone,
  icon: Icon,
}: {
  label: string
  value: string
  sub?: string
  tone: 'slate' | 'green' | 'red' | 'blue'
  icon: typeof Package
}) {
  const tones: Record<string, string> = {
    slate: 'border-slate-200 bg-white text-slate-700',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    red: 'border-rose-200 bg-rose-50 text-rose-700',
    blue: 'border-sky-200 bg-sky-50 text-sky-700',
  }
  const iconBg: Record<string, string> = {
    slate: 'bg-slate-100 text-slate-500',
    green: 'bg-emerald-100 text-emerald-600',
    red: 'bg-rose-100 text-rose-600',
    blue: 'bg-sky-100 text-sky-600',
  }
  return (
    <div className={`flex items-start gap-3 rounded-xl border p-4 ${tones[tone]}`}>
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconBg[tone]}`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] font-medium uppercase tracking-wide opacity-70">{label}</div>
        <div className="truncate text-xl font-bold leading-tight">{value}</div>
        {sub && <div className="text-xs opacity-70">{sub}</div>}
      </div>
    </div>
  )
}

/** Bara plus/minus proportionala cu valorile, per document. */
function PlusMinusBar({ plus, minus }: { plus: number; minus: number }) {
  const total = plus + Math.abs(minus)
  if (total === 0) return <div className="h-1.5 w-full rounded bg-slate-100" />
  const pPlus = (plus / total) * 100
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded bg-slate-100">
      <div className="bg-emerald-400" style={{ width: `${pPlus}%` }} />
      <div className="bg-rose-400" style={{ width: `${100 - pPlus}%` }} />
    </div>
  )
}

function DocCard({ doc }: { doc: InvDoc }) {
  const [open, setOpen] = useState(false)
  const netTone =
    doc.netValue > 0 ? 'text-emerald-600' : doc.netValue < 0 ? 'text-rose-600' : 'text-slate-500'
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-slate-50"
      >
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
          <ClipboardCheck className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-semibold text-slate-800">{doc.docNo}</span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-500">
              {doc.docType}
            </span>
          </div>
          <div className="truncate text-xs text-slate-500">
            {fmtDate(doc.date)}
            {doc.partner ? ` · ${doc.partner}` : ''} · {doc.lines.length} articole
          </div>
          <div className="mt-1.5 max-w-xs">
            <PlusMinusBar plus={doc.plusValue} minus={doc.minusValue} />
          </div>
        </div>
        <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
          <div className="flex items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-600">
              <TrendingUp className="h-3 w-3" /> {doc.plusItems}
            </span>
            <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 font-medium text-rose-600">
              <TrendingDown className="h-3 w-3" /> {doc.minusItems}
            </span>
          </div>
          <div className={`text-sm font-bold ${netTone}`}>{fmtRon(doc.netValue)}</div>
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2 font-medium">Articol</th>
                <th className="px-3 py-2 font-medium">Lot</th>
                <th className="px-3 py-2 text-right font-medium">Diferență</th>
                <th className="px-3 py-2 text-right font-medium">Cost unitar</th>
                <th className="px-4 py-2 text-right font-medium">Valoare</th>
              </tr>
            </thead>
            <tbody>
              {doc.lines.map((l, i) => {
                const up = l.qty > 0
                const down = l.qty < 0
                return (
                  <tr key={`${l.itmid}-${i}`} className="border-t border-slate-50">
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-800">
                        {l.name || l.sku || l.itmid || '—'}
                      </div>
                      {l.sku && l.name && <div className="text-xs text-slate-400">{l.sku}</div>}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{l.lot || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <span
                        className={`inline-flex items-center gap-1 font-semibold ${
                          up ? 'text-emerald-600' : down ? 'text-rose-600' : 'text-slate-500'
                        }`}
                      >
                        {up && <TrendingUp className="h-3.5 w-3.5" />}
                        {down && <TrendingDown className="h-3.5 w-3.5" />}
                        {l.qty > 0 ? '+' : ''}
                        {num.format(l.qty)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600">{fmtRon(l.unitCost)}</td>
                    <td
                      className={`px-4 py-2 text-right font-medium ${
                        l.value > 0
                          ? 'text-emerald-600'
                          : l.value < 0
                            ? 'text-rose-600'
                            : 'text-slate-500'
                      }`}
                    >
                      {fmtRon(l.value)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

interface ProbeResult {
  range: { from: string; to: string }
  totalLines: number
  doctypes: Array<{ doct: string; count: number; docName: string; sampleDocn: string }>
  sample: unknown[]
}

export function InventarDiferentePage() {
  const [from, setFrom] = useState(firstOfMonthIso())
  const [to, setTo] = useState(todayIso())
  const [probe, setProbe] = useState<ProbeResult | null>(null)
  const [probing, setProbing] = useState(false)
  const [probeErr, setProbeErr] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [docnInput, setDocnInput] = useState('')

  async function runExport() {
    setExporting(true)
    try {
      await downloadXlsx(
        `/ea/inventory-diffs?export=1&from=${from}&to=${to}`,
        `diferente-inventar-${from}_${to}.xlsx`,
      )
    } catch (e) {
      toast.error((e as Error).message || 'Export eșuat')
    } finally {
      setExporting(false)
    }
  }
  const [docnResult, setDocnResult] = useState<Record<string, unknown> | null>(null)
  const [docnLoading, setDocnLoading] = useState(false)

  async function runProbe() {
    setProbing(true)
    setProbeErr(null)
    try {
      const res = await api.get<ProbeResult>('/ea/inventory-diffs', {
        params: { from, to, probe: 1 },
        timeout: 90_000,
      })
      setProbe(res.data)
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string }
      setProbeErr(err.response?.data?.error?.message ?? err.message ?? 'Eroare necunoscută')
    } finally {
      setProbing(false)
    }
  }

  async function runDocnTest() {
    if (!docnInput.trim()) return
    setDocnLoading(true)
    setDocnResult(null)
    try {
      const res = await api.get<Record<string, unknown>>('/ea/inventory-diffs', {
        params: { probe: 'docn', docn: docnInput.trim() },
        timeout: 90_000,
      })
      setDocnResult(res.data)
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string }
      setDocnResult({
        error: err.response?.data?.error?.message ?? err.message ?? 'Eroare necunoscută',
      })
    } finally {
      setDocnLoading(false)
    }
  }

  const query = useQuery<InvDiffResponse>({
    queryKey: ['inventory-diffs', from, to],
    queryFn: async () =>
      (
        await api.get<InvDiffResponse>('/ea/inventory-diffs', {
          params: { from, to },
          timeout: 90_000,
        })
      ).data,
  })

  const data = query.data
  const errMsg = useMemo(() => {
    if (!query.isError) return null
    const e = query.error as {
      response?: { data?: { error?: { message?: string } } }
      message?: string
    }
    return e.response?.data?.error?.message ?? e.message ?? 'Eroare necunoscută'
  }, [query.isError, query.error])

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-b4l-blue/10 text-b4l-blue">
            <Boxes className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Diferențe inventar</h1>
            <p className="text-sm text-slate-500">
              Documente de inventariere (live din ExpertAccounts)
            </p>
          </div>
        </div>
        <div className="flex items-end gap-2">
          <label className="flex flex-col text-xs text-slate-500">
            De la
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-0.5 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-700"
            />
          </label>
          <label className="flex flex-col text-xs text-slate-500">
            Până la
            <input
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              className="mt-0.5 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-700"
            />
          </label>
          <button
            type="button"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className="flex items-center gap-1.5 rounded-lg bg-b4l-blue px-3 py-2 text-sm font-medium text-white transition hover:bg-b4l-blue/90 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} />
            Reîncarcă
          </button>
          <button
            type="button"
            onClick={runProbe}
            disabled={probing}
            title="Vezi ce tipuri de documente expune EA în perioada selectată"
            className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
          >
            <Stethoscope className={`h-4 w-4 ${probing ? 'animate-pulse' : ''}`} />
            Diagnostic
          </button>
          <button
            type="button"
            onClick={runExport}
            disabled={exporting || query.isFetching}
            title="Descarcă un Excel cu diferențele, documentele și liniile"
            className="flex items-center gap-1.5 rounded-lg border border-emerald-600 px-3 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-60"
          >
            <FileSpreadsheet className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Se exportă…' : 'Export Excel'}
          </button>
        </div>
      </div>

      <DateQuickButtons
        fromValue={from}
        toValue={to}
        onChange={(f, t) => {
          setFrom(f)
          setTo(t)
        }}
      />

      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <label className="flex flex-col text-xs text-slate-500">
          Test: caută un document după număr (ex. un stc cunoscut)
          <input
            type="text"
            value={docnInput}
            onChange={(e) => setDocnInput(e.target.value)}
            placeholder="nr. document (docn)"
            className="mt-0.5 w-64 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-700"
          />
        </label>
        <button
          type="button"
          onClick={runDocnTest}
          disabled={docnLoading || !docnInput.trim()}
          className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-white disabled:opacity-60"
        >
          <Stethoscope className={`h-4 w-4 ${docnLoading ? 'animate-pulse' : ''}`} />
          Testează documentul
        </button>
        {docnResult && (
          <div className="w-full">
            {typeof docnResult.error === 'string' ? (
              <p className="text-sm text-rose-700">{docnResult.error}</p>
            ) : (
              <p className="text-sm">
                {Number(docnResult.rawCount ?? 0) > 0 ? (
                  <span className="text-emerald-700">
                    ✓ get_invdocs ÎL conține: {String(docnResult.rawCount)} linii, tipuri{' '}
                    {JSON.stringify(docnResult.doctypes)} — deci putem folosi get_invdocs!
                  </span>
                ) : (
                  <span className="text-rose-700">
                    ✗ get_invdocs NU întoarce documentul „{String(docnResult.docn)}” — confirmat că
                    nu conține documentele de inventariere.
                  </span>
                )}
              </p>
            )}
          </div>
        )}
      </div>

      {(probe || probeErr) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-semibold text-amber-800">
              Diagnostic EA — tipuri de documente ({probe?.range.from} → {probe?.range.to})
            </span>
            <button
              type="button"
              onClick={() => {
                setProbe(null)
                setProbeErr(null)
              }}
              className="text-xs text-amber-700 underline"
            >
              închide
            </button>
          </div>
          {probeErr ? (
            <p className="text-rose-700">{probeErr}</p>
          ) : probe && probe.doctypes.length === 0 ? (
            <p className="text-amber-800">
              EA nu a întors niciun document în această perioadă ({probe.totalLines} linii).
              Încearcă un interval de date mai larg.
            </p>
          ) : (
            <>
              <p className="mb-2 text-amber-800">
                {probe?.totalLines} linii. Codul <code className="font-mono">stc</code> apare mai
                jos? Dacă inventarul are alt cod, spune-mi-l.
              </p>
              <div className="flex flex-wrap gap-2">
                {probe?.doctypes.map((d) => (
                  <span
                    key={d.doct}
                    className={`rounded-lg border px-2 py-1 text-xs ${
                      d.doct === 'stc'
                        ? 'border-emerald-300 bg-emerald-100 text-emerald-800'
                        : 'border-amber-300 bg-white text-slate-700'
                    }`}
                    title={`${d.docName} · ex: ${d.sampleDocn}`}
                  >
                    <span className="font-mono font-semibold">{d.doct}</span> · {d.count}
                    <span className="ml-1 text-slate-400">({d.docName})</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {errMsg && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {errMsg}
        </div>
      )}

      {query.isLoading && (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-16 text-slate-500">
          <RefreshCw className="h-5 w-5 animate-spin" /> Se încarcă din ExpertAccounts…
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiCard
              label="Documente"
              value={String(data.totals.docsCount)}
              sub={`${data.totals.itemsCount} linii`}
              tone="blue"
              icon={ClipboardCheck}
            />
            <KpiCard
              label="Plus inventar"
              value={fmtRon(data.totals.totalPlus)}
              tone="green"
              icon={TrendingUp}
            />
            <KpiCard
              label="Minus inventar"
              value={fmtRon(data.totals.totalMinus)}
              tone="red"
              icon={TrendingDown}
            />
            <KpiCard
              label="Diferență netă"
              value={fmtRon(data.totals.net)}
              tone={data.totals.net < 0 ? 'red' : 'green'}
              icon={ArrowDownUp}
            />
          </div>

          {data.documents.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white py-16 text-slate-400">
              <Package className="h-8 w-8" />
              <p className="text-sm">Niciun document de inventariere în perioada selectată.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {data.documents.map((doc) => (
                <DocCard key={doc.docNo} doc={doc} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
